import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useContext } from 'react';
import UserContext from './UserContext';
import Logout from './Logout';
import useSWR from 'swr';
import Fetcher from '../lib/Fetcher';
import { useServiceCounts } from '../hooks/useServiceCounts';
import logo from '../images/logo.png';
import {
  FaStore, FaTimes, FaBars, FaChevronDown, FaEnvelope, FaPhone, FaFileAlt,
  FaInfoCircle, FaHandsHelping, FaTags, FaDumbbell, FaHeartbeat, FaFlask, FaBook, FaEllipsisH,
  FaSpinner, FaUtensils, FaRunning, FaMagic, FaStar, FaSeedling, FaGlobe, FaUser,
  FaSignInAlt, FaUserPlus, FaUserTie, FaBoxOpen, FaSignOutAlt
} from 'react-icons/fa';


function LandingHeader() {
  const serviceCounts = useServiceCounts();
  const location = useLocation();
  const navigate = useNavigate();
  const { session, setSession } = useContext(UserContext);
  const { data, error } = useSWR("/auth/session", Fetcher);

  // Sync session with context if fetched successfully
  useEffect(() => {
    if (data && !session) {
      setSession(data);
    }
  }, [data, session, setSession]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [isPWA, setIsPWA] = useState(false);
  const isLoggedIn = !!session;
  const menuRef = useRef(null);
  const dropdownTimerRef = useRef(null);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);


  // Add this useEffect to handle mobile menu
  useEffect(() => {
    if (location.state?.openServices) {
      // For mobile devices
      if (window.innerWidth < 768) {
        setIsMenuOpen(true);
        setTimeout(() => {
          setActiveDropdown('services-mobile');
        }, 300);
      }
      // For desktop (your existing logic)
      else {
        setActiveDropdown('services');
      }

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);


  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
        setActiveSubDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // CHANGED: Simplified dropdown toggle logic
  const toggleDropdown = (dropdownName, e) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    if (activeDropdown !== dropdownName) {
      setActiveSubDropdown(null);
    }
  };

  const toggleSubDropdown = (subDropdownName, e) => {
    e.stopPropagation();
    setActiveSubDropdown(activeSubDropdown === subDropdownName ? null : subDropdownName);
  };

  // CHANGED: Improved hover handling
  const handleDropdownHover = (dropdownName, isEnter) => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
    }

    if (isEnter) {
      setActiveDropdown(dropdownName);
    } else {
      dropdownTimerRef.current = setTimeout(() => {
        setActiveDropdown(null);
        setActiveSubDropdown(null);
      }, 300);
    }
  };

  // CHANGED: Added function to close all dropdowns
  const closeAllDropdowns = () => {
    setActiveDropdown(null);
    setActiveSubDropdown(null);
  };

  return (
    <header className={`sticky top-0 ${isPWA ? 'z-[9999]' : 'z-[100]'}`}>
      {/* Main Header */}
      <div className={`${isPWA ? 'bg-gradient-to-r from-slate-800 via-blue-900 to-slate-900 border-b-2 border-cyan-400/40' : 'bg-gradient-to-r from-dark to-primary'} text-white ${isPWA ? 'shadow-2xl' : 'shadow-lg'} relative overflow-visible z-[100]`}>
        {isPWA && (
          <>
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-cyan-300 via-blue-300 via-indigo-300 to-transparent"></div>
            {/* Decorative gradient overlays */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-white/15 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-white/15 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-3xl"></div>
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }}></div>
          </>
        )}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`flex justify-between items-center ${isPWA ? 'py-5 sm:py-6 md:py-7' : 'py-3'}`}>
            {/* Logo and Brand */}
            <div className={`flex items-center ${isPWA ? 'gap-4 sm:gap-5' : ''}`}>
              <NavLink
                to="https://youtube.com/shorts/7qhTsTAMDoM?si=cTJo43ZBnXbDF5UI"
                className="relative group/logo flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                {isPWA && (
                  <>
                    {/* Enhanced glow effects for PWA */}
                    <div className="absolute -inset-2 bg-gradient-to-br from-white/50 via-white/30 to-transparent rounded-full blur-xl opacity-60 group-hover/logo:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute -inset-1 bg-gradient-to-br from-white/40 to-transparent rounded-full blur-lg opacity-70 group-hover/logo:opacity-90 transition-opacity duration-300"></div>
                    {/* Ring effect */}
                    <div className="absolute -inset-1.5 border-2 border-white/20 rounded-full opacity-0 group-hover/logo:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`absolute -inset-1 ${isPWA ? 'bg-gradient-to-br from-white/30 to-transparent blur-[10px]' : 'bg-gradient-to-br from-white/30 to-transparent blur-[10px]'} rounded-full ${isPWA ? 'opacity-80' : 'opacity-70'} group-hover/logo:opacity-100 transition-opacity duration-300`}></div>
                <div className={`relative transform ${isPWA ? 'group-hover/logo:scale-105' : 'group-hover/logo:scale-[1.02]'} transition-all duration-300 ${isPWA ? 'drop-shadow-[0_4px_12px_rgba(255,255,255,0.4)]' : 'drop-shadow-[0_2px_4px_rgba(255,255,255,0.3)]'}`}>
                  <img
                    src={logo}
                    alt="Yogvital Logo"
                    className={`${isPWA ? 'h-14 sm:h-16 md:h-18' : 'h-10'} w-auto ${isPWA ? 'drop-shadow-[0_4px_12px_rgba(255,255,255,0.5)]' : 'drop-shadow-[0_2px_4px_rgba(255,255,255,0.3)]'}`}
                  />
                </div>
              </NavLink>

              <NavLink
                to="/"
                className={`relative group/text ${isPWA ? 'ml-0' : 'ml-3'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {isPWA && (
                  <>
                    {/* Enhanced glow effects for PWA */}
                    <div className="absolute -inset-2 bg-gradient-to-br from-white/30 via-white/20 to-transparent rounded-xl blur-lg opacity-50 group-hover/text:opacity-80 transition-opacity duration-300"></div>
                    {/* Decorative accent bars - positioned symmetrically to avoid logo overlap */}
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 -translate-x-2 w-1 h-8 bg-gradient-to-b from-teal-300 via-blue-300 to-indigo-300 rounded-full opacity-0 group-hover/text:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 translate-x-2 w-1 h-8 bg-gradient-to-b from-teal-300 via-blue-300 to-indigo-300 rounded-full opacity-0 group-hover/text:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`absolute -inset-1 ${isPWA ? 'bg-gradient-to-br from-white/20 to-transparent blur-[6px]' : 'bg-gradient-to-br from-white/20 to-transparent blur-[6px]'} rounded-lg ${isPWA ? 'opacity-60' : 'opacity-50'} group-hover/text:opacity-80 transition-opacity duration-300`}></div>
                <h1 className={`relative ${isPWA ? 'text-3xl sm:text-4xl md:text-5xl' : 'text-xl md:text-2xl'} font-bold ${isPWA ? 'group-hover/text:text-white drop-shadow-[0_2px_8px_rgba(255,255,255,0.5)] tracking-tight' : 'group-hover/text:text-accent'} transition-all duration-300 ${isPWA ? 'px-2' : ''}`}>
                  Yogvital
                </h1>
              </NavLink>
            </div>

            {/* Desktop Navigation - Language and Account */}
            <div className={`hidden md:flex items-center ${isPWA ? 'space-x-4' : 'space-x-6'}`}>
              <div className={`flex items-center space-x-1 ${isPWA ? 'text-base' : 'text-sm'} ${isPWA ? 'text-gray-800 hover:text-gray-900 font-medium' : 'text-white/80 hover:text-white'} transition-colors`}>
                <FaGlobe className={isPWA ? 'text-base' : 'text-sm'} />
                <span>English</span>
              </div>
              <div
                className={`relative ${isPWA ? 'z-[10000]' : 'z-[110]'}`}
                onMouseEnter={() => handleDropdownHover('account', true)}
                onMouseLeave={() => handleDropdownHover('account', false)}
              >
                <button
                  className="flex items-center text-white hover:text-accent transition-colors text-sm md:text-base font-medium group"
                  onClick={(e) => toggleDropdown('account', e)}
                >
                  <div className="relative group-hover:scale-110 transition-transform">
                    <FaUser className="mr-2" />
                  </div>
                  <span>My Account</span>
                  <FaChevronDown className={`ml-1 text-xs transition-transform ${activeDropdown === 'account' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'account' && (
                  <div
                    className={`absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl ${isPWA ? 'z-[10000]' : 'z-[110]'} overflow-hidden border-2 ${isPWA ? 'border-teal-200/50' : 'border-white/20'}`}
                    onMouseEnter={() => handleDropdownHover('account', true)}
                    onMouseLeave={() => handleDropdownHover('account', false)}
                  >
                    {isLoggedIn && (
                      <NavLink
                        to="/orders"
                        className="flex items-start px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 border-b border-white/10 group"
                        onClick={closeAllDropdowns}
                      >
                        <div className="bg-purple-100 p-2 rounded-full mt-1">
                          <FaBoxOpen className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium">My Orders</span>
                          <span className="text-xs text-gray-500 group-hover:text-gray-700">Track & View Orders</span>
                        </div>
                      </NavLink>
                    )}
                    {isLoggedIn && (
                      <button
                        onClick={() => {
                          Logout(navigate, setSession);
                          closeAllDropdowns();
                        }}
                        className="w-full flex items-start px-4 py-3 hover:bg-red-50 text-dark transition-all gap-3 border-b border-white/10 group"
                      >
                        <div className="bg-red-100 p-2 rounded-full mt-1">
                          <FaSignOutAlt className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="flex flex-col text-left">
                          <span className="font-medium">Logout</span>
                          <span className="text-xs text-gray-500 group-hover:text-gray-700">Sign out of session</span>
                        </div>
                      </button>
                    )}
                    {!isLoggedIn && (
                      <>
                        <NavLink
                          to="/login"
                          className="flex items-start px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 border-b border-white/10 group"
                          onClick={closeAllDropdowns}
                        >
                          <div className="bg-primary/10 p-2 rounded-full mt-1">
                            <FaSignInAlt className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium">Sign In</span>
                            <span className="text-xs text-gray-500 group-hover:text-gray-700">Access Your Account</span>
                          </div>
                        </NavLink>
                        <NavLink
                          to="/signup"
                          className="flex items-start px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 group"
                          onClick={closeAllDropdowns}
                        >
                          <div className="bg-red-100 p-2 rounded-full mt-1">
                            <FaUserPlus className="h-5 w-5 text-red-600" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium">Create Account</span>
                            <span className="text-xs text-gray-500 group-hover:text-gray-700">Join Yogvital Today</span>
                          </div>
                        </NavLink>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Navigation - Marketplace, Cart, Account, and Hamburger Menu */}
            <div className={`md:hidden flex items-center ${isPWA ? 'space-x-3' : 'space-x-4'}`}>
              <NavLink
                to="/Market"
                className={`flex items-center ${isPWA ? 'text-white hover:text-white/90' : 'text-white hover:text-accent'} transition-colors ${isPWA ? 'text-base font-medium' : 'text-sm font-medium'} group relative`}
              >
                <div className={`relative group-hover:scale-110 transition-transform ${isPWA ? 'p-2 bg-white/10 rounded-lg' : ''}`}>
                  <FaStore className={isPWA ? 'h-6 w-6' : 'h-5 w-5'} />
                </div>
              </NavLink>

              <div className="relative">
                <button
                  className="flex items-center text-white hover:text-accent transition-colors text-sm font-medium group"
                  onClick={(e) => toggleDropdown('account-mobile', e)}
                >
                  <div className="relative group-hover:scale-110 transition-transform">
                    <FaUser className="h-5 w-5" />
                  </div>
                </button>
                {activeDropdown === 'account-mobile' && (
                  <div className={`absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl ${isPWA ? 'z-[10000]' : 'z-50'} overflow-hidden border-2 ${isPWA ? 'border-teal-200/50' : 'border-white/20'}`}>
                    {isLoggedIn && (
                      <button
                        className="w-full flex items-start px-4 py-3 hover:bg-red-50 text-dark transition-all gap-3 border-b border-white/10 group text-left"
                        onClick={() => {
                          setIsMenuOpen(false);
                          closeAllDropdowns();
                          Logout(navigate, setSession);
                        }}
                      >
                        <div className="bg-red-100 p-2 rounded-full mt-1">
                          <FaSignOutAlt className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium">Logout</span>
                        </div>
                      </button>
                    )}
                    {!isLoggedIn && (
                      <>
                        <NavLink
                          to="/login"
                          className="flex items-start px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 border-b border-white/10 group"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full mt-1">
                            <FaSignInAlt className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium">Sign In</span>
                            <span className="text-xs text-gray-500 group-hover:text-gray-700">Access Your Account</span>
                          </div>
                        </NavLink>
                        <NavLink
                          to="/signup"
                          className="flex items-start px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 group"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-red-100 p-2 rounded-full mt-1">
                            <FaUserPlus className="h-5 w-5 text-red-600" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium">Create Account</span>
                            <span className="text-xs text-gray-500 group-hover:text-gray-700">Join Yogvital Today</span>
                          </div>
                        </NavLink>
                      </>
                    )}
                  </div>
                )}
              </div>
              {/* Hide hamburger menu in PWA mode */}
              {!isPWA && (
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-white focus:outline-none p-2 rounded-full hover:bg-white/10 transition-colors"
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? (
                    <FaTimes className="w-5 h-5" />
                  ) : (
                    <FaBars className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Secondary Black Navigation Bar */}
      <div className={`${isPWA ? 'bg-gray-100 border-b-2 border-gray-300' : 'bg-black'} ${isPWA ? 'text-gray-800' : 'text-white'} ${isPWA ? 'shadow-xl' : 'shadow-lg'} relative overflow-visible z-[90]`}>
        {isPWA && (
          <>
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-300 via-blue-300 via-indigo-300 to-transparent"></div>
            {/* Decorative gradient overlays */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-200/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-28 h-28 bg-gradient-to-tr from-gray-200/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-gray-200/10 to-transparent rounded-full blur-3xl"></div>
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, gray 1px, transparent 0)',
              backgroundSize: '20px 20px'
            }}></div>
          </>
        )}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-end">
            <div className={`hidden md:flex items-center ${isPWA ? 'space-x-5' : 'space-x-8'} ${isPWA ? 'py-4 sm:py-5' : 'py-3'}`}>
              <NavLink
                to="/Market"
                className={`${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-all flex items-center ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} group relative`}
              >
                {isPWA && (
                  <>
                    {/* Enhanced glow effect */}
                    <div className="absolute -inset-1 bg-gradient-to-br from-white/20 to-transparent rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {/* Bottom accent line on hover */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-300 via-blue-300 to-indigo-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`relative group-hover:scale-110 transition-all duration-300 ${isPWA ? 'p-2.5 bg-white/10 hover:bg-white/20 rounded-xl mr-2.5 shadow-lg ring-2 ring-white/10 group-hover:ring-white/20' : ''}`}>
                  <FaStore className={isPWA ? 'text-xl' : 'mr-2'} />
                </div>
                {!isPWA && <span className="font-medium">Marketplace</span>}
                {isPWA && <span className="font-semibold relative">Marketplace</span>}
              </NavLink>


              <div
                className={`relative ${isPWA ? 'z-[10000]' : ''}`}
                onMouseEnter={() => handleDropdownHover('category', true)}
                onMouseLeave={() => handleDropdownHover('category', false)}
              >
                <button
                  className={`flex items-center ${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-all ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} font-medium group relative`}
                  onClick={(e) => toggleDropdown('category', e)}
                >
                  <div className={`relative group-hover:scale-110 transition-all duration-300 ${isPWA ? 'p-2.5 bg-white/10 hover:bg-white/20 rounded-xl mr-2.5 shadow-lg ring-2 ring-white/10 group-hover:ring-white/20' : ''}`}>
                    <FaTags className={isPWA ? 'text-xl' : 'mr-2'} />
                  </div>
                  {!isPWA && <span>Category</span>}
                  {isPWA && <span className="font-semibold relative">Category</span>}
                  <FaChevronDown className={`${isPWA ? 'ml-2' : 'ml-1'} text-xs transition-transform ${activeDropdown === 'category' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'category' && (
                  <div
                    className={`absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl ${isPWA ? 'z-[10000]' : 'z-50'} overflow-y-auto max-h-96 ${isPWA ? 'border border-white/20' : 'border-2 border-white/20'} p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20`}
                    onMouseEnter={() => handleDropdownHover('category', true)}
                    onMouseLeave={() => handleDropdownHover('category', false)}
                  >
                    {/* Instructors Submenu */}
                    <div className="relative border-b border-white/10">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 text-dark hover:bg-primary/10 transition-all"
                        onClick={(e) => toggleSubDropdown('instructors', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaDumbbell className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Instructors</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'instructors' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'instructors' && (
                        <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
<NavLink
                            to="/services/yoga-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaSpinner className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/zumba-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaRunning className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/reiki-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaMagic className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Reiki Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-trainer'] !== undefined ? `(${serviceCounts['reiki-trainer']})` : '(0)'}</span></span>
                          </NavLink>

                          <NavLink
                            to="/services/home-tuition"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaBook className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                          </NavLink>

                          <NavLink
                            to="/services/tarot-card-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaStar className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Tarot Card Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-trainer'] !== undefined ? `(${serviceCounts['tarot-card-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/gym-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaDumbbell className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                          </NavLink>
</div>
                      )}
                    </div>

                    {/* Consultants Submenu */}
                    <div className="relative border-t border-white/10">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 text-dark hover:bg-primary/10 transition-all"
                        onClick={(e) => toggleSubDropdown('consultants', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaHandsHelping className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Consultants</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'consultants' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'consultants' && (
                        <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
<NavLink
                            to="/services/dietitian"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaUtensils className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/astrologer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaStar className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/reiki-healer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaMagic className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/ayurvedacharya"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaHeartbeat className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/numerologer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaSeedling className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/tarot-card-reader"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={closeAllDropdowns}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaStar className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                          </NavLink>
                           <NavLink
                             to="/services/home-tutor"
                             className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                             onClick={closeAllDropdowns}
                           >
                             <div className="bg-primary/10 p-2 rounded-full">
                               <FaBook className="h-5 w-5 text-primary" />
                             </div>
                             <span className="font-medium">Home Tutor <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tutor'] !== undefined ? `(${serviceCounts['home-tutor']})` : '(0)'}</span></span>
                           </NavLink>
</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Services Dropdown */}
              <div
                className={`relative ${isPWA ? 'z-[10000]' : ''}`}
                onMouseEnter={() => handleDropdownHover('services', true)}
                onMouseLeave={() => handleDropdownHover('services', false)}
              >
                <button
                  className={`flex items-center ${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-all ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} font-medium group relative`}
                  onClick={(e) => toggleDropdown('services', e)}
                >
                  <div className={`relative group-hover:scale-110 transition-all duration-300 ${isPWA ? 'p-2.5 bg-white/10 hover:bg-white/20 rounded-xl mr-2.5 shadow-lg ring-2 ring-white/10 group-hover:ring-white/20' : ''}`}>
                    <FaHandsHelping className={isPWA ? 'text-xl' : 'mr-2'} />
                  </div>
                  {!isPWA && <span>Services</span>}
                  {isPWA && <span className="font-semibold relative">Services</span>}
                  <FaChevronDown className={`${isPWA ? 'ml-2' : 'ml-1'} text-xs transition-transform ${activeDropdown === 'services' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'services' && (
                  <div
                    className={`absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl ${isPWA ? 'z-[10000]' : 'z-50'} overflow-hidden ${isPWA ? 'border border-white/20' : 'border-2 border-white/20'}`}
                    onMouseEnter={() => handleDropdownHover('services', true)}
                    onMouseLeave={() => handleDropdownHover('services', false)}
                  >
                    <div className="max-h-[360px] overflow-y-auto overflow-x-hidden">
                      {/* Fitness Submenu */}
                      <div className="relative border-b border-white/10">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-fitness', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaDumbbell className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Fitness</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-fitness' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-fitness' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/yoga-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaSpinner className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/dietitian"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaUtensils className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/gym-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaDumbbell className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/zumba-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaRunning className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>

                      {/* Wellness Submenu */}
                      <div className="relative border-b border-white/10">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-wellness', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaMagic className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Wellness</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-wellness' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-wellness' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/reiki-healer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaMagic className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/reiki-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaMagic className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Reiki Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-trainer'] !== undefined ? `(${serviceCounts['reiki-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/astrologer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaStar className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/numerologer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaSeedling className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/tarot-card-reader"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaStar className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                            </NavLink>

                            <NavLink
                              to="/services/tarot-card-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaStar className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Tarot Card Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-trainer'] !== undefined ? `(${serviceCounts['tarot-card-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>

                      {/* Medical Submenu */}
                      <div className="relative border-b border-white/10">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-medical', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaFlask className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Medical</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-medical' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-medical' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/ayurvedacharya"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaHeartbeat className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>

                      {/* Knowledge Submenu */}
                      <div className="relative">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-knowledge', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaBook className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Knowledge</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-knowledge' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-knowledge' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/home-tuition"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaBook className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/home-tutor"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={closeAllDropdowns}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaBook className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Home Tutor <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tutor'] !== undefined ? `(${serviceCounts['home-tutor']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <NavLink
                to="/about"
                className={`${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-all flex items-center ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} group relative`}
              >
                <div className={`relative group-hover:scale-110 transition-all duration-300 ${isPWA ? 'p-2.5 bg-white/10 hover:bg-white/20 rounded-xl mr-2.5 shadow-lg ring-2 ring-white/10 group-hover:ring-white/20' : ''}`}>
                  <FaInfoCircle className={isPWA ? 'text-xl' : 'mr-2'} />
                </div>
                {!isPWA && <span className="font-medium">About Us</span>}
                {isPWA && <span className="font-semibold relative">About Us</span>}
              </NavLink>

              <div
                className={`relative ${isPWA ? 'z-[10000]' : ''}`}
                onMouseEnter={() => handleDropdownHover('contact', true)}
                onMouseLeave={() => handleDropdownHover('contact', false)}
              >
                <button
                  className={`flex items-center ${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-all ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} font-medium group relative`}
                  onClick={(e) => toggleDropdown('contact', e)}
                >
                  <div className={`relative group-hover:scale-110 transition-all duration-300 ${isPWA ? 'p-2.5 bg-white/10 hover:bg-white/20 rounded-xl mr-2.5 shadow-lg ring-2 ring-white/10 group-hover:ring-white/20' : ''}`}>
                    <FaEnvelope className={isPWA ? 'text-xl' : 'mr-2'} />
                  </div>
                  {!isPWA && <span>Contact Us</span>}
                  {isPWA && <span className="font-semibold relative">Contact Us</span>}
                </button>
                {activeDropdown === 'contact' && (
                  <div
                    className={`absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl ${isPWA ? 'z-[10000]' : 'z-50'} overflow-y-auto max-h-96 ${isPWA ? 'border border-white/20' : 'border-2 border-white/20'} p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20`}
                    onMouseEnter={() => handleDropdownHover('contact', true)}
                    onMouseLeave={() => handleDropdownHover('contact', false)}
                  >
                    <a
                      href="mailto:contact@yogvital.com"
                      className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 border-b border-white/10 hover:translate-x-1 hover:shadow-md"
                    >
                      <div className="bg-primary/10 p-2 rounded-full">
                        <FaEnvelope className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Email Us</p>
                        <p className="text-xs text-gray-500">contact@yogvital.com</p>
                      </div>
                    </a>
                    <a
                      href="tel:+918923162316"
                      className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 border-b border-white/10 hover:translate-x-1 hover:shadow-md"
                    >
                      <div className="bg-primary/10 p-2 rounded-full">
                        <FaPhone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Call Us</p>
                        <p className="text-xs text-gray-500">+91 89231 62316</p>
                      </div>
                    </a>
                    <NavLink
                      to="/contact-form"
                      className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                      onClick={closeAllDropdowns}
                    >
                      <div className="bg-primary/10 p-2 rounded-full">
                        <FaFileAlt className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Contact Form</p>
                        <p className="text-xs text-gray-500">Send us a message</p>
                      </div>
                    </NavLink>
                  </div>
                )}
              </div>

              {/* My Orders Link - Moved to end */}
              {isLoggedIn && (
                <NavLink
                  to="/orders"
                  className={`${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-all flex items-center ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} group relative ml-4`}
                >
                  <div className={`relative group-hover:scale-110 transition-all duration-300 ${isPWA ? 'p-2.5 bg-white/10 hover:bg-white/20 rounded-xl mr-2.5 shadow-lg ring-2 ring-white/10 group-hover:ring-white/20' : ''}`}>
                    <FaBoxOpen className={isPWA ? 'text-xl' : 'mr-2'} />
                  </div>
                  {!isPWA && <span className="font-medium">My Orders</span>}
                  {isPWA && <span className="font-semibold relative">My Orders</span>}
                </NavLink>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Menu Content - Hidden in PWA mode */}
      {!isPWA && isMenuOpen && (
        <div
          ref={menuRef}
          className={`md:hidden bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl border-2 ${isPWA ? 'border-teal-200/50' : 'border-white/20'} mx-2 mb-2 overflow-y-auto max-h-[80vh] transition-all duration-300 p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20 ${isPWA ? 'z-[10000]' : 'z-50'}`}
        >
          <div className="space-y-1 p-2">
            <NavLink
              to="/Market"
              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="bg-primary/10 p-2 rounded-full">
                <FaStore className="h-5 w-5 text-primary" />
              </div>
              <span className="font-medium">Marketplace</span>
            </NavLink>


            <div className="relative">
              <button
                className="flex items-center justify-between w-full px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                onClick={(e) => toggleDropdown('category-mobile', e)}
              >
                <div className="flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <FaTags className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-medium ml-2">Category</span>
                </div>
                <FaChevronDown className={`text-xs transition-transform ${activeDropdown === 'category-mobile' ? 'transform rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'category-mobile' && (
                <div className="ml-4 mt-1 space-y-1 bg-white/90 rounded-lg overflow-y-auto max-h-80 p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20 animate-fade-in">
                  {/* Instructors Submenu */}
                  <div className="relative border-b border-primary/10">
                    <button
                      className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-dark transition-all rounded-lg"
                      onClick={(e) => toggleSubDropdown('instructors-mobile', e)}
                    >
                      <div className="flex items-center gap-2">
                        <FaDumbbell className="h-5 w-5 text-primary" />
                        <p className="text-base font-semibold">Instructors</p>
                      </div>
                      <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'instructors-mobile' ? 'transform rotate-180' : ''}`} />
                    </button>
                    {activeSubDropdown === 'instructors-mobile' && (
                      <div className="bg-white/95 pl-4 rounded-lg border-l-2 border-primary/20">
<NavLink
                          to="/services/yoga-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaSpinner className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/zumba-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaRunning className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/reiki-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaMagic className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Reiki Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-trainer'] !== undefined ? `(${serviceCounts['reiki-trainer']})` : '(0)'}</span></span>
                        </NavLink>

                        <NavLink
                          to="/services/home-tuition"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaBook className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                        </NavLink>

                        <NavLink
                          to="/services/tarot-card-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaStar className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Tarot Card Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-trainer'] !== undefined ? `(${serviceCounts['tarot-card-trainer']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/gym-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaDumbbell className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                        </NavLink>
</div>
                    )}
                  </div>

                  {/* Consultants Submenu */}
                  <div className="relative">
                    <button
                      className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-dark transition-all rounded-lg"
                      onClick={(e) => toggleSubDropdown('consultants-mobile', e)}
                    >
                      <div className="flex items-center gap-2">
                        <FaHandsHelping className="h-5 w-5 text-primary" />
                        <p className="text-base font-semibold">Consultants</p>
                      </div>
                      <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'consultants-mobile' ? 'transform rotate-180' : ''}`} />
                    </button>
                    {activeSubDropdown === 'consultants-mobile' && (
                      <div className="bg-white/95 pl-4 rounded-lg border-l-2 border-primary/20">
<NavLink
                          to="/services/dietitian"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaUtensils className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/astrologer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaStar className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/reiki-healer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaMagic className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/ayurvedacharya"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaHeartbeat className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/numerologer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaSeedling className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                        </NavLink>
<NavLink
                          to="/services/tarot-card-reader"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaStar className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/home-tutor"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaBook className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Home Tutor <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tutor'] !== undefined ? `(${serviceCounts['home-tutor']})` : '(0)'}</span></span>
                        </NavLink>
</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                className="flex items-center justify-between w-full px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                onClick={(e) => toggleDropdown('services-mobile', e)}
              >
                <div className="flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <FaHandsHelping className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-medium ml-2">Services</span>
                </div>
                <FaChevronDown className={`text-xs transition-transform ${activeDropdown === 'services-mobile' ? 'transform rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'services-mobile' && (
                <div className="ml-4 mt-1 space-y-1 bg-white/90 rounded-lg overflow-y-auto max-h-80 p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20 animate-fade-in">
                  {/* Fitness Submenu */}
                  <div className="relative border-b border-primary/10">
                    <button
                      className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-dark transition-all rounded-lg"
                      onClick={(e) => toggleSubDropdown('services-mobile-fitness', e)}
                    >
                      <div className="flex items-center gap-2">
                        <FaDumbbell className="h-5 w-5 text-primary" />
                        <p className="text-base font-semibold">Fitness</p>
                      </div>
                      <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-fitness' ? 'transform rotate-180' : ''}`} />
                    </button>
                    {activeSubDropdown === 'services-mobile-fitness' && (
                      <div className="bg-white/95 pl-4 rounded-lg border-l-2 border-primary/20">
                        <NavLink
                          to="/services/yoga-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaSpinner className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/dietitian"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaUtensils className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/gym-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaDumbbell className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/zumba-trainer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaRunning className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                        </NavLink>
                      </div>
                    )}
                  </div>

                  {/* Wellness Submenu */}
                  <div className="relative border-b border-primary/10">
                    <button
                      className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-dark transition-all rounded-lg"
                      onClick={(e) => toggleSubDropdown('services-mobile-wellness', e)}
                    >
                      <div className="flex items-center gap-2">
                        <FaMagic className="h-5 w-5 text-primary" />
                        <p className="text-base font-semibold">Wellness</p>
                      </div>
                      <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-wellness' ? 'transform rotate-180' : ''}`} />
                    </button>
                    {activeSubDropdown === 'services-mobile-wellness' && (
                      <div className="bg-white/95 pl-4 rounded-lg border-l-2 border-primary/20">
                        <NavLink
                          to="/services/reiki-healer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaMagic className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/astrologer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaStar className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/numerologer"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaSeedling className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/tarot-card-reader"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaStar className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                        </NavLink>
                      </div>
                    )}
                  </div>

                  {/* Medical Submenu */}
                  <div className="relative border-b border-primary/10">
                    <button
                      className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-dark transition-all rounded-lg"
                      onClick={(e) => toggleSubDropdown('services-mobile-medical', e)}
                    >
                      <div className="flex items-center gap-2">
                        <FaFlask className="h-5 w-5 text-primary" />
                        <p className="text-base font-semibold">Medical</p>
                      </div>
                      <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-medical' ? 'transform rotate-180' : ''}`} />
                    </button>
                    {activeSubDropdown === 'services-mobile-medical' && (
                      <div className="bg-white/95 pl-4 rounded-lg border-l-2 border-primary/20">
                        <NavLink
                          to="/services/ayurvedacharya"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaHeartbeat className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                        </NavLink>
                      </div>
                    )}
                  </div>

                  {/* Knowledge Submenu */}
                  <div className="relative">
                    <button
                      className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-dark transition-all rounded-lg"
                      onClick={(e) => toggleSubDropdown('services-mobile-knowledge', e)}
                    >
                      <div className="flex items-center gap-2">
                        <FaBook className="h-5 w-5 text-primary" />
                        <p className="text-base font-semibold">Knowledge</p>
                      </div>
                      <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-knowledge' ? 'transform rotate-180' : ''}`} />
                    </button>
                    {activeSubDropdown === 'services-mobile-knowledge' && (
                      <div className="bg-white/95 pl-4 rounded-lg border-l-2 border-primary/20">
                        <NavLink
                          to="/services/home-tuition"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaBook className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                        </NavLink>
                        <NavLink
                          to="/services/home-tutor"
                          className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                          onClick={() => {
                            setIsMenuOpen(false);
                            closeAllDropdowns();
                          }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full">
                            <FaBook className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium">Home Tutor <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tutor'] !== undefined ? `(${serviceCounts['home-tutor']})` : '(0)'}</span></span>
                        </NavLink>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/about"
              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="bg-primary/10 p-2 rounded-full">
                <FaInfoCircle className="h-5 w-5 text-primary" />
              </div>
              <span className="font-medium">About Us</span>
            </NavLink>

            <div className="relative">
              <button
                className="flex items-center justify-between w-full px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                onClick={(e) => toggleDropdown('contact-mobile', e)}
              >
                <div className="flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <FaEnvelope className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-medium ml-2">Contact Us</span>
                </div>
                <FaChevronDown className={`text-xs transition-transform ${activeDropdown === 'contact-mobile' ? 'transform rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'contact-mobile' && (
                <div className="ml-4 mt-1 space-y-1 bg-white/90 rounded-lg overflow-hidden animate-fade-in">
                  <a
                    href="mailto:contact@yogvital.com"
                    className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="bg-primary/10 p-2 rounded-full">
                      <FaEnvelope className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-medium">Email Us</span>
                  </a>
                  <a
                    href="tel:+918923162316"
                    className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="bg-primary/10 p-2 rounded-full">
                      <FaPhone className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-medium">Call Us</span>
                  </a>
                  <NavLink
                    to="/contact-form"
                    className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                    onClick={() => {
                      setIsMenuOpen(false);
                      closeAllDropdowns();
                    }}
                  >
                    <div className="bg-primary/10 p-2 rounded-full">
                      <FaFileAlt className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-medium">Contact Form</span>
                  </NavLink>
                </div>
              )}
            </div>
            {isLoggedIn && (
              <NavLink
                to="/orders"
                className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="bg-primary/10 p-2 rounded-full">
                  <FaBoxOpen className="h-5 w-5 text-primary" />
                </div>
                <span className="font-medium">My Orders</span>
              </NavLink>
            )}
          </div>
        </div >
      )
      }

      {/* WhatsApp button removed - now integrated into ChatBot component for unified support experience */}
    </header >
  );
}

export default LandingHeader;